import {fromUnixTime, setDefaultOptions} from 'date-fns';
import {formatInTimeZone} from 'date-fns-tz';
import {timeZone} from "../../Constants/Date";
import {fr} from 'date-fns/locale';
import {TimeSlot} from "../../Types/TimeSlot";
import {GroupDeliveryDay} from "../../Types/GroupDeliveryDay";

setDefaultOptions({locale: fr});

/**
 * Converts a timestamp or Date object to a formatted string in a specific time zone.
 *
 * @param {number | Date} tms - The timestamp or Date object to format.
 * @param {string} [formatString] - The format string to use. Defaults to 'eeee dd MMMM' if not provided.
 * @returns {string | undefined} - The formatted date string or undefined if the input is invalid.
 */
export const tmsToFormat = (tms: number | Date, formatString?: string): string | undefined => {
    if (!tms) return undefined;

    const date = tms instanceof Date ? tms : fromUnixTime(tms);
    const adjustedFormatString = date.getMinutes() === 0 ? (formatString || 'eeee dd MMMM').replace('mm', '') : (formatString || 'eeee dd MMMM');

    return formatInTimeZone(date, timeZone, adjustedFormatString);
};

/**
 * Formats a TimeSlot object into a string representation.
 *
 * @param {TimeSlot} timeSlot - The TimeSlot object containing date, startHour, endHour, and theoricalDate.
 * @param {Object} [options] - Optional settings for formatting.
 * @param {boolean} [options.condensed=false] - Whether to use a condensed date format.
 * @param {boolean} [options.showHours=false] - Whether to show hours in the formatted string.
 * @param {boolean} [options.theorical=false] - Whether to use the theorical date for formatting.
 * @returns {string} - The formatted TimeSlot string.
 */
export const formatTimeSlot = (timeSlot: TimeSlot, options?: {
    condensed?: boolean, showHours?: boolean, theorical?: boolean
}): string => {
    const {condensed = false, showHours = false, theorical = false} = options || {};
    if(!timeSlot || !timeSlot.startHour || !timeSlot.endHour) return 'Indisponible';
    const {date, startHour, endHour, theoricalDate} = timeSlot;

    const dateFormat = condensed ? 'eee dd MMM' : 'eeee dd MMMM';
    const dateFormatted = tmsToFormat(theorical ? theoricalDate : date, dateFormat) || 'Indisponible';

    if (condensed) return dateFormatted;
    if (showHours) {
        const startHourFormatted = tmsToFormat(startHour, 'HH\'h\'mm');
        const endHourFormatted = tmsToFormat(endHour, 'HH\'h\'mm');
        return `${dateFormatted} entre ${startHourFormatted} et ${endHourFormatted}`;
    }

    return dateFormatted;
};

/**
 * Formats a GroupDeliveryDay object into a string representation.
 *
 * @param {GroupDeliveryDay} gdd - The GroupDeliveryDay object containing dayLabel, startHour, and endHour.
 * @param {Object} [options] - Optional settings for formatting.
 * @param {boolean} [options.condensed=false] - Whether to use a condensed date format.
 * @returns {string} - The formatted GroupDeliveryDay string.
 */
export const formatGDD = (gdd: GroupDeliveryDay, options?: {
    condensed?: boolean,
    showHours?: boolean
}): string => {
    const {condensed = false, showHours = false} = options || {};
    if (!gdd || !gdd.dayLabel) return 'Indisponible';
    const dayLabel = gdd.dayLabel?.toLowerCase();
    if (condensed) return `les ${dayLabel}s`;
    if (showHours) {
        const startHour = tmsToFormat(gdd.startHour, 'HH\'h\'mm');
        const endHour = tmsToFormat(gdd.endHour, 'HH\'h\'mm');
        return `tous les ${dayLabel}s, entre ${startHour} et ${endHour}`;
    }
    return `tous les ${dayLabel}s`;
};

export default { tmsToFormat, formatTimeSlot, formatGDD };
